/**
 * ModernResultsFull - Comprehensive Results Display (Orchestrator)
 *
 * Used for Pro tiers (Drama+, DramaX) with full analysis details.
 * Orchestrates section components with tier-based visibility.
 *
 * Phase 3: Mobile Results Optimization
 * - Refactored as orchestrator (sections extracted to ./sections/)
 * - Hooks extracted to ./hooks/
 * - Ready for QuickNav (Phase 3.2), truncation (Phase 3.3), accordions (Phase 3.4)
 *
 * Phase 4: Theatrical Mode Integration (Feb 2026)
 * - Uses useNarratorDisplay for tier-aware narrator text
 * - Supports future streaming handoff
 * - Voice-styled text based on tier (playful, balanced, professional)
 *
 * Design: Y2K + Glassmorphism aesthetic (matches ModernResultsPreview)
 * Accessibility: WCAG AAA compliant
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { View, Platform, Text, StyleSheet, Dimensions } from 'react-native';
import { TierBody } from '@dramadetector/ui';
import {
  getLikelyImpactLabel,
  type DramaAnalysisResponse,
  type DramaCategory,
  type MessageContext,
  type ResultModuleKey,
  type ResultsPrimaryActionTreatment,
  type ShareTreatment,
} from '@dramadetector/shared';
import { captureEvent } from '@/lib/analytics';
import { useNarratorDisplay } from '@/hooks/useNarratorDisplay';
import {
  useResultsFunnelAnalytics,
  type ResultsFunnelSurface,
} from '@/hooks/useResultsFunnelAnalytics';
import type { UserTier } from '@/lib/narrator/loadingQuips';
import { useNarratorReactions } from '@/lib/narrator/reactions';

import { GlassButton } from '../glass';
import { ViralShareButton } from '../ViralShareButton';
import { ModernScoreCard } from './ModernScoreCard';
import { PrimaryRewriteCard } from './PrimaryRewriteCard';
import { NarratorReactionCard } from './NarratorReactionCard';
import { NarratorFloatingPill } from './NarratorFloatingPill';
import { sharedStyles, getCategoryFromScore, voiceStyleColors } from './sharedStyles';

// Section components
import {
  GatedPatternsSection,
  TransformationsSection,
} from './sections';

// Feedback with persistence (database-backed)
import { FeedbackButtons } from '../FeedbackButtons';

// Inline Annotations (Priority C - Dec 2025)
import { GatedInlineAnnotations } from './inline-annotations';
import { selectPrimaryRewriteDisplay } from './inline-annotations/utils/mergeIssuesWithRewrites';
import type { RevisionTrackingConfig } from './inline-annotations/InlineMessageDisplay';

function toLikelyImpactCategory(
  category: string | null | undefined,
): DramaCategory | null {
  switch (category) {
    case 'safe':
    case 'caution':
    case 'high_drama':
    case 'extreme_drama':
      return category;
    default:
      return null;
  }
}

interface ModernResultsFullProps {
  /** Analysis response data */
  analysis: DramaAnalysisResponse;
  /** Original message text (for inline annotations) */
  message: string;
  /** Message context (family, work, etc.) */
  context?: MessageContext;
  /** Current tier level (1=Free, 2=Drama+, 3=DramaX) */
  tierLevel: number;
  /** User's tier string for inline annotations */
  tier?: 'anonymous' | 'free' | 'drama_plus' | 'dramax' | 'family_member';
  /** Whether analysis is in progress (for narrator loading state) */
  isLoading?: boolean;
  /** Streamed narrator text (partial, for future streaming support) */
  streamedNarrator?: string;
  /** Whether streaming is enabled */
  streamingEnabled?: boolean;
  /** Callback when "Analyze Another" is clicked */
  onNewAnalysis: () => void;
  /** Callback when upgrade is pressed on gated content */
  onUpgrade?: () => void;
  /** Enable L3 accordion sections (A/B test: l3_accordions_enabled flag) */
  l3AccordionsEnabled?: boolean;
  /** Translation function */
  t: (key: string, options?: Record<string, unknown>) => string;
  /** Optional revision tracking configuration (enables RLHF data collection) */
  tracking?: RevisionTrackingConfig;
  /** Test ID for testing */
  testID?: string;
  /** Hide the built-in viral share button (used on shared-link pages) */
  hideShareButton?: boolean;
  /** Shared-policy top-level module order for rollback-safe hierarchy changes. */
  moduleOrder?: ResultModuleKey[];
  /** Whether likely-impact framing should be shown near the score. */
  showLikelyImpact?: boolean;
  /** Whether the best rewrite card should be promoted near the top. */
  showPrimaryRewriteCard?: boolean;
  /** Shared-policy share emphasis seam for rollback-safe CTA ordering. */
  shareTreatment?: ShareTreatment;
  /** Shared-policy primary action seam for copy/use-over-share rollout. */
  primaryActionTreatment?: ResultsPrimaryActionTreatment;
  /** Whether this render belongs to home, private results, or shared-link. */
  analyticsSurface?: ResultsFunnelSurface;
}

/**
 * ModernResultsFull - Comprehensive results orchestrator for Pro users
 *
 * Sections (tier-aware):
 * 1. Score Card (all tiers)
 * 2. Summary Card (all tiers)
 * 3. Patterns Section (all tiers, blur-gated for L1 via GatedPatternsSection)
 * 4. Transformations Section (L3 tier-gated)
 * 5. Feedback Buttons (all tiers)
 * 6. Share Button (all tiers)
 * 7. Action Button (all tiers)
 */
export function ModernResultsFull({
  analysis,
  message,
  context,
  tierLevel,
  tier = 'free',
  isLoading = false,
  streamedNarrator,
  streamingEnabled = false,
  onNewAnalysis,
  onUpgrade,
  l3AccordionsEnabled = false,
  t,
  tracking,
  testID = 'modern-results-full',
  hideShareButton = false,
  moduleOrder,
  showLikelyImpact = false,
  showPrimaryRewriteCard = false,
  shareTreatment,
  primaryActionTreatment = 'legacy',
  analyticsSurface = 'results_page',
}: ModernResultsFullProps) {
  // Extract data from nested `result` (unified pipeline) or flat structure (legacy)
  // Unified pipeline: analysis.result.score, Legacy: analysis.score
  const score = analysis.result?.score ?? analysis.score;
  const issues = analysis.result?.issues ?? analysis.issues ?? [];
  const rewrites = analysis.result?.rewrites ?? analysis.rewrites ?? [];
  const summary = analysis.result?.summary ?? analysis.summary;
  const categoryFromResult = analysis.result?.category ?? analysis.category;

  const category = categoryFromResult || getCategoryFromScore(score);
  const resolvedShareTreatment: ShareTreatment = hideShareButton
    ? 'hidden'
    : shareTreatment ?? 'secondary';
  const shouldPromotePrimaryActions =
    primaryActionTreatment === 'copy_use_primary';
  const { trackPrimaryRewriteCopied } = useResultsFunnelAnalytics({
    analysis,
    tier,
    surface: analyticsSurface,
    renderVariant: 'full',
    showLikelyImpact,
    showPrimaryRewriteCard,
    primaryActionTreatment,
    shareTreatment: resolvedShareTreatment,
  });
  const likelyImpactLabel = showLikelyImpact
    ? getLikelyImpactLabel(toLikelyImpactCategory(category))
    : null;
  const primaryRewrite = useMemo(
    () =>
      showPrimaryRewriteCard
        ? selectPrimaryRewriteDisplay(
            message,
            issues,
            rewrites,
            analysis.transformations,
          )
        : null,
    [showPrimaryRewriteCard, message, issues, rewrites, analysis.transformations],
  );
  const topSectionOrder = useMemo<ResultModuleKey[]>(
    () =>
      moduleOrder?.filter(
        (moduleKey) =>
          moduleKey === 'summary' ||
          moduleKey === 'primary_rewrite' ||
          moduleKey === 'narrator',
      ) ?? ['narrator', 'summary'],
    [moduleOrder],
  );

  // Detect circuit breaker response (crisis detection returns score: null)
  const isCircuitBreakerResponse = score === null;

  // Use narrator display hook for tier-aware narrator text with theatrical mode integration
  // Pass t function for i18n translations of loading quips
  const userTier = tier as UserTier;
  const {
    displayText: narratorText,
    displayState,
    voiceStyle,
    showTypingAnimation,
  } = useNarratorDisplay({
    tier: userTier,
    isAnalyzing: isLoading,
    analysisResult: {
      narrator_intro: analysis.result?.narrator_intro ?? analysis.narrator_intro,
      summary: summary,
    },
    streamedNarrator,
    streamingEnabled,
    t,
  });

  // Extract narrator_intro for display (when complete, show both fields for testing)
  const narratorIntro = analysis.result?.narrator_intro ?? analysis.narrator_intro;

  // Display text during loading/streaming phase
  const loadingText = displayState === 'loading' || displayState === 'streaming'
    ? narratorText
    : null;

  // Determine what sections to show based on tier
  const hasIssues = issues && issues.length > 0;
  const hasPatterns = analysis.patterns && analysis.patterns.length > 0;
  const hasTransformations =
    analysis.transformations && analysis.transformations.length > 0 && tierLevel >= 3;

  // Score Chase: Live score delta state from revision tracking
  const [liveRevisedScore, setLiveRevisedScore] = useState<number | null>(null);
  const [liveScoreDelta, setLiveScoreDelta] = useState(0);

  // Narrator reactions: track rewrite session activity
  const [acceptedCount, setAcceptedCount] = useState(0);
  const [isRescoring, setIsRescoring] = useState(false);
  const [sessionComplete, setSessionComplete] = useState(false);
  const [lastAcceptedSeverity, setLastAcceptedSeverity] = useState<string | undefined>();

  const handleScoreChange = useCallback((
    revisedScore: number,
    delta: number,
    meta?: { acceptedCount: number; isRescoring: boolean; sessionComplete: boolean; lastAcceptedSeverity?: string },
  ) => {
    setLiveRevisedScore(revisedScore);
    setLiveScoreDelta(delta);
    if (meta) {
      setAcceptedCount(meta.acceptedCount);
      setIsRescoring(meta.isRescoring);
      setSessionComplete(meta.sessionComplete);
      setLastAcceptedSeverity(meta.lastAcceptedSeverity);
    }
  }, []);

  // Narrator live reactions hook (pass raw tier for family_member -> l2 mapping)
  const narratorReactions = useNarratorReactions({
    tier: tier ?? 'free',
    narratorIntro: narratorIntro ?? null,
    originalScore: score ?? 0,
    revisedScore: liveRevisedScore ?? score ?? 0,
    scoreDelta: liveScoreDelta,
    acceptedCount,
    isRescoring,
    sessionComplete,
    lastAcceptedSeverity,
  });

  // Track time on results for CTA tracking
  const resultsStartTime = useRef(Date.now());

  // Narrator card ref + visibility tracking for floating pill
  const narratorCardRef = useRef<View>(null);
  const [isNarratorCardVisible, setIsNarratorCardVisible] = useState(true);

  // Detect if narrator card is off-screen using measure()
  // Called periodically or on scroll events from parent
  const checkNarratorVisibility = useCallback(() => {
    if (!narratorCardRef.current) {
      setIsNarratorCardVisible(true);
      return;
    }
    narratorCardRef.current.measure((
      _x: number,
      _y: number,
      _width: number,
      height: number,
      _pageX: number,
      pageY: number,
    ) => {
      // Card is visible if any part is within viewport (0 to window height)
      // pageY is the Y position relative to the screen
      if (pageY === undefined || height === undefined) return;
      const screenHeight = Platform.OS === 'web' ? window.innerHeight : Dimensions.get('window').height;
      const isVisible = pageY + height > 0 && pageY < screenHeight;
      setIsNarratorCardVisible(isVisible);
    });
  }, []);

  // Session is active when user has accepted at least one rewrite
  const isRewriteSessionActive = acceptedCount > 0;

  // Scroll to narrator card on pill tap
  const scrollToNarratorCard = useCallback(() => {
    if (Platform.OS === 'web' && narratorCardRef.current) {
      const node = narratorCardRef.current as unknown as {
        scrollIntoView?: (opts: ScrollIntoViewOptions) => void;
      };
      node.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    }
  }, []);

  // Periodically check narrator visibility when rewrite session is active.
  // Uses a 500ms interval since we don't have direct scroll event access
  // from the parent ScrollView.
  useEffect(() => {
    if (!isRewriteSessionActive) return;

    const intervalId = setInterval(checkNarratorVisibility, 500);
    return () => clearInterval(intervalId);
  }, [isRewriteSessionActive, checkNarratorVisibility]);

  // Track CTA clicks with timing
  const handleAnalyzeAnother = useCallback(() => {
    captureEvent('Results_CTA_Click', {
      cta_type: 'analyze_another',
      tier: tier ?? 'anonymous',
      platform: Platform.OS as 'web' | 'ios' | 'android',
      time_on_results_ms: Date.now() - resultsStartTime.current,
      score,
      category,
    });
    onNewAnalysis();
  }, [tier, score, category, onNewAnalysis]);

  // Track upgrade CTA clicks
  const handleUpgrade = useCallback(() => {
    captureEvent('Results_CTA_Click', {
      cta_type: 'upgrade',
      tier: tier ?? 'anonymous',
      platform: Platform.OS as 'web' | 'ios' | 'android',
      time_on_results_ms: Date.now() - resultsStartTime.current,
      score,
      category,
    });
    onUpgrade?.();
  }, [tier, score, category, onUpgrade]);

  const renderTopSection = (moduleKey: ResultModuleKey, index: number) => {
    switch (moduleKey) {
      case 'summary':
        return (displayState === 'idle' || displayState === 'complete') && summary ? (
          <View
            key={`${moduleKey}-${index}`}
            style={sharedStyles.glassCard}
            role="region"
            accessibilityLabel={t('home.modern.analysis_summary', { defaultValue: 'Analysis Summary' })}
            testID={`${testID}-summary-card`}
          >
            <TierBody
              size="base"
              color={voiceStyleColors['professional']}
              style={[
                narratorStyles.text,
                narratorStyles.professionalText,
              ]}
            >
              {summary}
            </TierBody>
          </View>
        ) : null;
      case 'primary_rewrite':
        return primaryRewrite ? (
          <View key={`${moduleKey}-${index}`}>
            <PrimaryRewriteCard
              rewriteText={primaryRewrite.rewritten}
              reasoning={primaryRewrite.reasoning}
              showCopyAction={shouldPromotePrimaryActions}
              onCopyAction={trackPrimaryRewriteCopied}
              t={t}
              testID={`${testID}-primary-rewrite-card`}
            />
          </View>
        ) : null;
      case 'narrator':
        return (displayState === 'idle' || displayState === 'complete') && narratorReactions.currentText ? (
          <View key={`${moduleKey}-${index}`}>
            <NarratorReactionCard
              ref={narratorCardRef}
              text={narratorReactions.currentText}
              voiceStyle={voiceStyle}
              energyLevel={narratorReactions.energyLevel}
              accessibilityLabel={t('home.modern.narrator_intro', { defaultValue: 'Analysis Introduction' })}
              testID={`${testID}-narrator-card`}
            />
          </View>
        ) : null;
      default:
        return null;
    }
  };

  const shareButton =
    !isCircuitBreakerResponse && resolvedShareTreatment !== 'hidden' ? (
      <View
        style={
          resolvedShareTreatment === 'primary'
            ? resultsFullStyles.primaryShareButtonContainer
            : resultsFullStyles.secondaryShareButtonContainer
        }
        testID={`${testID}-share-container`}
      >
        <ViralShareButton
          analysis={analysis}
          context={context}
          analysisId={analysis.id}
          variant={resolvedShareTreatment === 'primary' ? 'primary' : 'secondary'}
          size={resolvedShareTreatment === 'primary' ? 'lg' : 'sm'}
          fullWidth={resolvedShareTreatment === 'primary'}
          testID={`${testID}-share-button`}
        />
      </View>
    ) : null;

  const primaryActions = (
    <View style={sharedStyles.actionButtons} testID={`${testID}-primary-actions`}>
      <GlassButton
        onPress={handleAnalyzeAnother}
        variant='primary'
        style={sharedStyles.actionButton}
        testID={`${testID}-new-analysis-button`}
      >
        {t('home.modern.analyze_another', { defaultValue: 'Analyze Another' })}
      </GlassButton>
    </View>
  );

  return (
    <View style={sharedStyles.container} testID={testID}>
      {/* Score Card */}
      <View>
        <ModernScoreCard
          score={score}
          revisedScore={liveRevisedScore}
          scoreDelta={liveScoreDelta}
          category={category}
          scoreLabel={t('home.modern.drama_score', { defaultValue: 'Drama Score' })}
          impactLabel={
            likelyImpactLabel
              ? t('home.modern.likely_impact_value', {
                  defaultValue: 'Likely impact: {impact}',
                  impact: likelyImpactLabel,
                })
              : null
          }
          isRescoring={isRescoring}
          acceptedCount={acceptedCount}
          totalIssues={issues?.length ?? 0}
          testID={`${testID}-score-card`}
        />
      </View>

      {/* Loading/Streaming Phase - Show narrator quips */}
      {loadingText && (
        <View
          style={sharedStyles.glassCard}
          role="region"
          accessibilityLabel={t('home.modern.analyzing', { defaultValue: 'Analyzing...' })}
          testID={`${testID}-loading-card`}
        >
          <TierBody
            size="base"
            color={voiceStyleColors[voiceStyle]}
            style={[
              narratorStyles.text,
              voiceStyle === 'playful' && narratorStyles.playfulText,
            ]}
          >
            {loadingText}
            {showTypingAnimation && (
              <Text style={narratorStyles.cursor}>|</Text>
            )}
          </TierBody>
        </View>
      )}

      {topSectionOrder.map(renderTopSection)}

      {/* Floating pill for off-screen narrator updates during rewrite session */}
      <NarratorFloatingPill
        text={narratorReactions.currentText}
        isCardVisible={isNarratorCardVisible}
        isSessionActive={isRewriteSessionActive}
        onTap={scrollToNarratorCard}
        trigger={narratorReactions.trigger === 'intro' ? 'intro' : narratorReactions.trigger}
        tier={tier}
        testID={`${testID}-narrator-pill`}
      />

      {/* Inline Annotations Section - Tier-gated (Priority C) */}
      {hasIssues && message && (
        <View
          style={sharedStyles.glassCard}
          role="region"
          accessibilityLabel={t('home.modern.inline_annotations', { defaultValue: 'Message Issues' })}
          testID={`${testID}-inline-annotations`}
        >
          <GatedInlineAnnotations
            message={message}
            issues={issues}
            rewrites={rewrites}
            overrideTier={tier}
            screen="results_full"
            onUpgrade={handleUpgrade}
            tracking={tracking}
            onScoreChange={handleScoreChange}
            testID={`${testID}-gated-annotations`}
          />
        </View>
      )}

      {/* Patterns Section - Tier-gated via GatedPatternsSection */}
      {hasPatterns && (
        <View>
          <GatedPatternsSection
            patterns={analysis.patterns!}
            t={t}
            overrideTier={tier}
            screen="results_full"
            onUpgrade={handleUpgrade}
            testID={`${testID}-patterns-section`}
          />
        </View>
      )}

      {/* Transformations Section (L3) */}
      {hasTransformations && (
        <View>
          <TransformationsSection
            transformations={analysis.transformations!}
            t={t}
            testID={`${testID}-transformations-section`}
            accordionEnabled={l3AccordionsEnabled}
          />
        </View>
      )}

      {/* Divider */}
      <View style={sharedStyles.sectionDivider} />

      {/* Feedback Section - Skip for circuit breaker responses */}
      {!isCircuitBreakerResponse && (
        <View>
          <FeedbackButtons
            analysisId={analysis.id}
            analysisScore={score}
            testID={`${testID}-feedback-buttons`}
          />
        </View>
      )}

      {shouldPromotePrimaryActions ? primaryActions : shareButton}
      {shouldPromotePrimaryActions ? shareButton : primaryActions}
    </View>
  );
}

/**
 * Narrator-specific styles for voice presentation
 */
const resultsFullStyles = StyleSheet.create({
  primaryShareButtonContainer: {
    maxWidth: 400,
    alignSelf: 'center',
    width: '100%',
    marginTop: 16,
  },
  secondaryShareButtonContainer: {
    marginTop: 16,
    alignSelf: 'center',
  },
});

const narratorStyles = StyleSheet.create({
  text: {
    lineHeight: 24,
  },
  playfulText: {
    fontStyle: 'italic',
  },
  professionalText: {
    fontWeight: '500',
  },
  cursor: {
    opacity: 0.6,
  },
  debugText: {
    fontSize: 10,
    color: '#9ca3af', // gray-400
    marginTop: 8,
  },
  // Style for narrator intro card (visually distinct from summary)
  narratorCard: {
    borderLeftWidth: 3,
    borderLeftColor: '#8b5cf6', // purple-500 (playful accent)
  },
});
